import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Gig, Milestone, MilestoneEvent, MilestoneStep, MilestoneStepFeedback } from '../../../../types/collaborations/CollaborationTypes'
import CurrentStep from '../step/CurrentStep'
import CampaignGig from '../components/CampaignGig';
import PropertiesPreview from '../components/PropertiesPreview';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux-stores/store';
import { SelectedStore } from '../gigs/Stores';
import { APP_NAME } from '../../../../constants/constants';
import { formatAmount } from '../../../../utils/helpers';

const MAX_HEIGHT = 160;

interface TimelineProps {
    active_steps: MilestoneStep[],
    events: MilestoneEvent[],
    milestone: Milestone,
    setOpenedStep: (slug: string) => void;
    onFeedbackView: (feedback: string) => void,
    addDeadline: (step: MilestoneStep) => void,
    onPlayBtnClick: (gig: Gig) => void
    setSelectedMedia: (media: any) => void
    refetch: () => void
}

interface TimelineEventProps {
    event: MilestoneEvent,
    getCompletedTaskComponent: (step: MilestoneStep, deliverables: any) => React.ReactNode,
    feedbacks: MilestoneStepFeedback[],
    onFeedbackView: (feedback: string) => void,

}

const TimelineEvent: React.FC<TimelineEventProps> = ({ event, getCompletedTaskComponent, feedbacks, onFeedbackView }) => {

    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const contentRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;

        const observer = new ResizeObserver(() => {
            setIsOverflowing(el.scrollHeight > MAX_HEIGHT);
        });

        observer.observe(el);

        return () => observer.disconnect();
    }, []);

    return (
        <div className='position-relative'>
            <div className={`step-card app-card p-0 step-card-completed`}>
                <div ref={contentRef} className={`step-card-2 timeline-event ${isExpanded ? 'expanded' : ''}`}>
                    <div className='d-flex flex-column'>
                        <span className={`${event.revision_required ? 'text-danger' : ''} gig-step-title`}>{event.step.gig_step.processed_title ?? (event.step.gig_step.title ?? (event.step.gig_step.name ?? '')) + '*'}</span>
                        <span className='processed-at-timestamp'>{event.created}</span>
                        {
                            event.feedback?.text &&
                            <button onClick={() => onFeedbackView(event?.feedback?.text ?? "")} className="btn btn-outline-primary fw-600 fs-12 mt-2">View Feedback</button>
                        }
                        {
                            getCompletedTaskComponent(event.step, event.deliverables)
                        }
                    </div>
                    {!isExpanded && isOverflowing && <div className="fade-overlay" />}

                </div>
                {isOverflowing && (
                    <div
                        className="read-more-button p-2 px-3 m-0"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        {isExpanded ? 'Show less' : 'Show more'}
                    </div>
                )}
                {
                    event.feedback?.reaction &&
                    <div className="emoji-bubble">
                        {
                            event.feedback.reaction === 'good' ? '👍' : (event.feedback.reaction === 'better' ? '❤️' : '🔥️')
                        }
                    </div>
                }
            </div>

            {
                event.revision_required ?
                    (
                        <div className='rejected-icon'>
                            <i className="fa-solid fa-xmark text-white"></i>
                        </div>
                    ) :
                    (
                        <div className='completed-icon'>
                            <i className="fa-solid fa-check text-white"></i>
                        </div>
                    )
            }

        </div>
    )
}

const Timeline: React.FC<TimelineProps> = ({ active_steps, events, milestone, setOpenedStep, onFeedbackView, addDeadline, onPlayBtnClick, setSelectedMedia, refetch }) => {

    const { campaignId, milestoneId } = useParams();

    const collaborationData = useSelector((state: RootState) => state.collaborationDetails.collaborations[campaignId!]?.collaborationData);
    const collaboration = collaborationData?.collaboration ?? null;

    // Redux Data //
    const milestoneData = useSelector((state: RootState) => state.collaborationMilestones.collaborations[campaignId!]?.[milestoneId!]?.data);

    const getCompletedTaskComponent = (completedTask: MilestoneStep, deliverables: any) => {
        if (['gig_selection', 'gig_addition'].includes(completedTask.gig_step.slug)) {

            if(milestone?.gig_type?.slug === 'store_visit'){
                return <SelectedStore gigs={milestone?.campaign_gigs} />
            }

            return (
                <div>
                    <div className="gigs-list">
                        {
                            milestone?.campaign_gigs.map((gig, i) => {
                                return (
                                    <CampaignGig gig={gig} gigType={milestone.gig_type.slug} onPlayBtnClick={onPlayBtnClick} quickRepost={!!milestone?.specifications?.quick_repost} currency={collaboration?.currency!} />
                                )
                            })
                        }
                    </div>
                </div>
            )
        } else {
            return <PropertiesPreview setSelectedMedia={setSelectedMedia} files={Array.isArray(milestone.files) ? milestone.files : []} deliverables={deliverables} step={completedTask} />
        }

        return null;
    }

    const payments = useMemo(() => {
        return Array.isArray(milestoneData?.payments) ? milestoneData.payments : [];
    }, [milestoneData?.payments])


    const getPlannedPayoutDate = () => {
        if (!milestone.planned_payout_date) return "-";
        const momentObj = moment(milestone.planned_payout_date);
        const isToday = momentObj.isSame(moment(), 'day');
        if (isToday) return "today";
        const isTomorrow = momentObj.isSame(moment().add(1, 'day'), 'day');
        if (isTomorrow) return "tomorrow";

        return "on " + momentObj.format("MMMM Do YYYY");

    }

    const pendingPayoutAmount = useMemo(() => {
        const amount = payments.reduce((sum, payment) => {
            if (payment.status === 'pending') {
                return sum + Number(payment.amount);
            }
            return sum;
        }, 0);
        return amount;
    }, [payments])

    return (
        <div className="d-flex flex-column gap-3 position-relative">
            <div className="timeline-line"></div>
            {
                [...active_steps].reverse().map((currentTask, i) => {
                    return (
                        <CurrentStep
                            onFeedbackView={onFeedbackView}
                            feedbacks={milestone.feedbacks}
                            currentStep={currentTask}
                            setOpenedStep={setOpenedStep}
                            addDeadline={() => addDeadline(currentTask)}
                            key={i}
                            refetch={refetch}
                        />
                    )
                })
            }
            {
                active_steps.length === 0 && milestone?.ended_at &&
                <div>
                    <div className={`step-card ${pendingPayoutAmount ? 'waiting' : 'completed'}`}>
                        <div className='step-card-1'>
                            {
                                pendingPayoutAmount ?
                                    (
                                        <div className="waiting-badge">
                                            Waiting
                                        </div>
                                    ) : (
                                        <div className="completed-badge">
                                            Completed
                                        </div>
                                    )
                            }
                        </div>
                        <div className="step-card-2">
                            <div className='d-flex flex-column'>
                                <span className='gig-step-title text-dark fs-16 fw-600'>{
                                    (pendingPayoutAmount && milestone.planned_payout_date) ? "You are expected to get paid on " + getPlannedPayoutDate() : "Milestone Completed"
                                }</span>
                                {
                                    pendingPayoutAmount ? (
                                        <span className="gig-step-description text-dark-gray fs-12 fw-400">
                                            Your total amount of {formatAmount(pendingPayoutAmount, collaboration?.currency?.code, collaboration?.currency?.symbol, collaboration?.currency?.locale)} will be transferred to your {APP_NAME} wallet.
                                        </span>
                                    ) : <span className='processed-at-timestamp'>{milestone?.ended_at}</span>
                                }
                            </div>
                        </div>
                    </div>

                </div>
            }
            {
                [...events].reverse().map((event, i) => {
                    return (
                        <TimelineEvent
                            key={i}
                            event={event}
                            getCompletedTaskComponent={getCompletedTaskComponent}
                            feedbacks={milestone.feedbacks ?? []}
                            onFeedbackView={onFeedbackView}
                        />
                    )
                })
            }
             <div className='position-relative'>
                <div className="app-card step-card-completed p-075 d-flex gap-1 flex-column">
                    <span className='gig-step-title'>Collaborated</span>
                    <span className='processed-at-timestamp'>{milestone?.starts_at}</span>
                    {
                        collaboration?.language &&
                        <div>
                            <strong>Language: </strong>
                            <span>{collaboration.language.name}</span>
                        </div>
                    }
                </div>
                <div className='completed-icon'>
                    <i className='fa-solid fa-check text-white' ></i>
                </div>
            </div>
        </div>
    )
}

export default Timeline